import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ExternalLink, Share2, Mail, MessageCircle, Download } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useSubdomain } from '../contexts/SubdomainContext';
import api from '../services/api';

/**
 * Share Selling Process — a small dashboard page where a logged-in user grabs
 * their personalized public link to /requests/info with their tracking code
 * (ref_user + ref_company). Same code RegisterPage reads on signup — so any
 * recipient who signs up from this link is attributed back to the inviter.
 *
 * The link uses the user's company subdomain when available; falls back to
 * www.trados.app otherwise.
 */
const BRAND_NAVY = '#1E2A35';
const BRAND_GOLD = '#F5A623';

const ShareSellingProcess = () => {
  const { user } = useAuth();
  const { company, isSubdomain } = useSubdomain();
  const [copied, setCopied] = useState(false);

  // The backend owns the short form (/selling/wave/7f3k2q) because the share
  // code lives on the user record. The long ?ref_user=&ref_company= URL is kept
  // as the fallback so the page still works if that call fails — and because
  // links already in the wild use it.
  const [shortLink, setShortLink] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/selling/my-link')
      .then((r) => { if (!cancelled) setShortLink(r.data); })
      .catch(() => { if (!cancelled) setShortLink(null); });
    return () => { cancelled = true; };
  }, []);

  const legacyUrl = useMemo(() => {
    const userId = user?.id;
    const companyId = user?.company_id;
    let baseUrl = 'https://www.trados.app';
    if (isSubdomain && company?.subdomain) baseUrl = `https://${company.subdomain}.trados.app`;
    else if (companyId && company?.subdomain) baseUrl = `https://${company.subdomain}.trados.app`;
    let link = `${baseUrl}/requests/info`;
    const params = [];
    if (userId) params.push(`ref_user=${userId}`);
    if (companyId) params.push(`ref_company=${companyId}`);
    if (params.length) link += '?' + params.join('&');
    return link;
  }, [user, company, isSubdomain]);

  const shareUrl = shortLink?.personal_url || legacyUrl;

  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Link copied.');
    } catch {
      toast.error('Could not copy the link.');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied — paste it into email, WhatsApp, anywhere.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const subject = encodeURIComponent('How our deals work on Trados');
  const body = encodeURIComponent(
    `Hi,\n\nBefore we start, please review how the deal will be conducted through the Trados verified-trade platform:\n\n${shareUrl}\n\nThanks,`
  );
  const mailtoHref = `mailto:?subject=${subject}&body=${body}`;
  const waHref = `https://wa.me/?text=${body}`;

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: BRAND_NAVY }}>Share Your Selling Process</h1>
            <p className="text-sm text-gray-500 mt-1">
              Send this link to any buyer or seller before you start. It explains the 10-step
              verified trade process — and every signup from your link is credited to you.
            </p>
          </div>

          {/* Personalized link card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
            <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: BRAND_GOLD }}>
              Your shareable link
            </p>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <code
                className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-700 break-all"
                data-testid="share-link"
              >
                {shareUrl}
              </code>
              <button
                onClick={copy}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors flex-shrink-0"
                style={{ background: BRAND_NAVY, color: '#fff' }}
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            {shortLink?.company_url && (
              <p className="mt-3 text-xs text-slate-500">
                Company-wide link (credits {shortLink.company_name || 'your company'} rather than you
                personally):{' '}
                <button
                  type="button"
                  onClick={() => copyValue(shortLink.company_url)}
                  data-testid="share-company-link"
                  className="font-mono text-slate-700 underline hover:text-slate-900 break-all"
                >
                  {shortLink.company_url}
                </button>
              </p>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <ExternalLink className="w-4 h-4" /> Preview page
              </a>
              <a
                href={mailtoHref}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <Mail className="w-4 h-4" /> Email it
              </a>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: BRAND_GOLD, color: BRAND_NAVY }}
                title="Opens the page; use the Download PDF button on it to print a tracked PDF"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            </div>
          </div>

          {/* Tracking explainer */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-start gap-3">
              <Share2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND_GOLD }} />
              <div>
                <h2 className="text-base font-semibold text-gray-900">How tracking works</h2>
                <ul className="text-sm text-gray-600 mt-2 space-y-1.5 list-disc pl-5">
                  <li>Your link carries <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">ref_user</code> + <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">ref_company</code> in the URL.</li>
                  <li>Anyone who clicks it and signs up is automatically credited to you (same system as the Invite Users page).</li>
                  <li>The signup CTA, the Sign-in link, and the URL printed on the PDF all carry your tracking code.</li>
                  <li>Track your referrals on the <strong>My Referrals</strong> page.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default ShareSellingProcess;
